import React, { useState } from 'react';
import { Trash2, Upload, Pencil, X, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/utils/image';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass = "w-full bg-black border border-white/10 rounded-none px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors";
const btnClass = "bg-white text-black hover:bg-gray-200 rounded-none px-6 py-3 text-sm uppercase tracking-widest transition-all duration-300";

const PackageEditor = ({ pkg, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: pkg.name, description: pkg.description, price: pkg.price });
  const [uploading, setUploading] = useState(false);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/packages/${pkg.id}`, form);
      toast.success('Package updated');
      setEditing(false);
      onRefresh();
    } catch { toast.error('Failed to update package'); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${pkg.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/packages/${pkg.id}`);
      toast.success('Package deleted');
      onRefresh();
    } catch { toast.error('Failed to delete package'); }
  };

  const handleAddImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/packages/${pkg.id}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Image added');
      onRefresh();
    } catch { toast.error('Failed to add image'); }
    finally { setUploading(false); }
  };

  const handleRemoveImage = async (imageUrl) => {
    try {
      await axios.delete(`${API}/packages/${pkg.id}/images`, { params: { image_url: imageUrl } });
      toast.success('Image removed');
      onRefresh();
    } catch { toast.error('Failed to remove image'); }
  };

  return (
    <div className="border border-white/10 p-6" data-testid={`package-editor-${pkg.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        {editing ? (
          <div className="flex-1 space-y-3">
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="Package name" data-testid="edit-pkg-name" />
            <input type="text" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputClass} placeholder="Price (e.g. £250)" data-testid="edit-pkg-price" />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={`${inputClass} resize-none`} placeholder="Description" data-testid="edit-pkg-desc" />
            <div className="flex gap-2">
              <button onClick={handleSave} className="flex items-center gap-1 bg-white text-black hover:bg-gray-200 px-4 py-2 text-xs uppercase tracking-widest transition-colors" data-testid="save-pkg-btn"><Check size={14} /> Save</button>
              <button onClick={() => { setEditing(false); setForm({ name: pkg.name, description: pkg.description, price: pkg.price }); }} className="flex items-center gap-1 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest hover:bg-white/10 transition-colors"><X size={14} /> Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="min-w-0">
              <h3 className="text-xl font-normal mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{pkg.name}</h3>
              <p className="text-lg text-white mb-2">{pkg.price}</p>
              <p className="text-sm text-gray-400 whitespace-pre-line">{pkg.description}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(true)} className="text-gray-500 hover:text-white transition-colors p-1" data-testid={`edit-pkg-${pkg.id}`}><Pencil size={16} /></button>
              <button onClick={handleDelete} className="text-gray-500 hover:text-red-400 transition-colors p-1" data-testid={`delete-pkg-${pkg.id}`}><Trash2 size={16} /></button>
            </div>
          </>
        )}
      </div>

      <div className="mt-4 border-t border-white/10 pt-4">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Package Images ({pkg.images.length})</p>
        <div className="flex gap-3 flex-wrap mb-3">
          {pkg.images.map((img, i) => (
            <div key={i} className="relative group w-20 h-20 shrink-0">
              <img src={resolveImageUrl(img)} alt={`${pkg.name} ${i + 1}`} className="w-full h-full object-cover" />
              <button onClick={() => handleRemoveImage(img)} className="absolute -top-1 -right-1 bg-red-600 hover:bg-red-700 text-white p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-pkg-img-${i}`}><X size={12} /></button>
            </div>
          ))}
        </div>
        <label className="block">
          <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) handleAddImage(f); e.target.value = ''; }} className="w-full bg-black border border-white/10 rounded-none px-3 py-2 text-white text-xs focus:outline-none focus:border-white/30 transition-colors file:mr-3 file:py-1 file:px-3 file:rounded-none file:border-0 file:text-xs file:bg-white file:text-black hover:file:bg-gray-200" disabled={uploading} data-testid={`add-pkg-img-${pkg.id}`} />
          {uploading && <p className="text-xs text-gray-400 mt-1">Uploading...</p>}
        </label>
      </div>
    </div>
  );
};

export const PackagesTab = ({ packages, onRefresh }) => {
  const [newPkg, setNewPkg] = useState({ name: '', description: '', price: '' });

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newPkg.name.trim() || !newPkg.price.trim()) return;
    try {
      await axios.post(`${API}/packages`, {
        name: newPkg.name.trim(),
        description: newPkg.description.trim(),
        price: newPkg.price.trim(),
      });
      toast.success('Package created');
      setNewPkg({ name: '', description: '', price: '' });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create package'); }
  };

  return (
    <div>
      <h2 className="text-2xl font-normal mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Manage Packages</h2>
      <p className="text-sm text-gray-400 mb-8">Create packages with photos, descriptions, and pricing shown on the Packages page.</p>

      <div className="border border-white/10 p-8 mb-10 max-w-xl">
        <h3 className="text-lg font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Add New Package</h3>
        <form onSubmit={handleAdd} className="space-y-4" data-testid="add-package-form">
          <input type="text" value={newPkg.name} onChange={(e) => setNewPkg({ ...newPkg, name: e.target.value })} placeholder="Package name" className={inputClass} required data-testid="new-pkg-name" />
          <input type="text" value={newPkg.price} onChange={(e) => setNewPkg({ ...newPkg, price: e.target.value })} placeholder="Price (e.g. £250, From £150)" className={inputClass} required data-testid="new-pkg-price" />
          <textarea value={newPkg.description} onChange={(e) => setNewPkg({ ...newPkg, description: e.target.value })} rows={3} placeholder="What's included in this package..." className={`${inputClass} resize-none`} data-testid="new-pkg-desc" />
          <button type="submit" className={`w-full ${btnClass} flex items-center justify-center gap-2`} data-testid="add-pkg-btn">
            <Upload size={16} /> Add Package
          </button>
        </form>
      </div>

      <h3 className="text-lg font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Current Packages ({packages.length})</h3>
      {packages.length === 0 ? (
        <p className="text-gray-400 text-sm">No packages yet.</p>
      ) : (
        <div className="space-y-6">
          {packages.map((pkg) => <PackageEditor key={pkg.id} pkg={pkg} onRefresh={onRefresh} />)}
        </div>
      )}
    </div>
  );
};

export default PackagesTab;
